import { Cpu, LineChart, ListTree, Table2 } from 'lucide-react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import { cn } from '@/lib/utils';

const TABS = [
  {
    id: 'analysis',
    label: 'Analysis trends',
    description: 'Cost, duration, and per-agent pipeline metrics',
    path: '/admin/instrumentation',
    icon: LineChart,
    match: (pathname: string) =>
      pathname === '/admin/instrumentation' || pathname === '/admin/instrumentation/',
  },
  {
    id: 'scripts',
    label: 'Script metrics',
    description: 'Per-script counts, scores, and emission health',
    path: '/admin/instrumentation/scripts',
    icon: Table2,
    match: (pathname: string) => pathname.startsWith('/admin/instrumentation/scripts'),
  },
  {
    id: 'steps',
    label: 'Step Analysis',
    description: 'Per-run methodology step ok / fall-through / failed',
    path: '/admin/instrumentation/steps',
    icon: ListTree,
    match: (pathname: string) => pathname.startsWith('/admin/instrumentation/steps'),
  },
  {
    id: 'ml',
    label: 'ML metrics',
    description: 'ML success, fall-through, and latency p95 per model',
    path: '/admin/instrumentation/ml',
    icon: Cpu,
    match: (pathname: string) => pathname.startsWith('/admin/instrumentation/ml'),
  },
] as const;

function InstrumentationTabs() {
  const location = useLocation();

  return (
    <nav
      className="mb-8 grid grid-cols-2 gap-2 border-b border-[#2a2a2a] pb-3 md:grid-cols-4"
      aria-label="Instrumentation types"
      data-testid="instrumentation-tabs"
      data-tab-count={TABS.length}
    >
      {TABS.map((tab) => {
        const Icon = tab.icon;
        const active = tab.match(location.pathname);

        return (
          <NavLink
            key={tab.id}
            to={tab.path}
            end={tab.id === 'analysis'}
            title={tab.description}
            className={cn(
              'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-sans transition-colors',
              active
                ? 'border-index-accent bg-index-accent/10 text-white'
                : 'border-[#2a2a2a] text-white/55 hover:border-white/25 hover:text-white/85',
            )}
          >
            <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
            <span>{tab.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export default function AdminInstrumentation() {
  return (
    <IndexWorkspaceShell activeNav="admin">
      <header className="mb-6">
        <p className="ni-label mb-2">Admin</p>
        <h1 className="text-2xl md:text-3xl font-sans font-bold text-white tracking-tight">
          Instrumentation
        </h1>
        <p className="mt-2 text-sm font-mono text-white/45 max-w-2xl">
          Pipeline performance, methodology step health, ML endpoint metrics, and per-script
          scorecard diagnostics.
        </p>
      </header>

      <InstrumentationTabs />
      <Outlet />
    </IndexWorkspaceShell>
  );
}
